import { useState } from 'react';
import axiosInstance from 'interceptor/axiosInstance';
import { ApiRoutes } from 'routes/routeConstants/apiRoutes';
import { ParentOrganization, Target } from 'models/Target/Target.model';
import { deserialize, serialize } from 'serializr';
import { generateYearOptions } from 'shared/utils/generateYearOptions';
import { generatePath } from 'react-router-dom';
import Notification from '../../shared/components/Notification';
import { NotificationTypes } from '../../enums/notificationTypes';
import { TargetCoverageMeta } from 'models/Target/targetCoverageMeta.model';
import { TargetYearsMeta } from 'models/Target/targetYearsmeta.model';
import { mapDesiredData } from 'shared/utils/convertScopeDropdownLables';

const TargetService = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [metaLoading, setMetaLoading] = useState<boolean>(false);
  const [parentOrgLoading, setParentOrgLoading] = useState<boolean>(false);
  const [isCustomButtonLoading, setIsCustomButtonLoading] =
    useState<boolean>(false);
  const [targetCoverageMeta, setTargetCoverageMeta] = useState<
    TargetCoverageMeta[]
  >([]);
  const [targetYearsMeta, setTargetYearsMeta] = useState<TargetYearsMeta[]>([]);
  const [organization, setOrganization] = useState<ParentOrganization>();

  const createTarget = async (payload: Target) => {
    try {
      setLoading(true);
      const desiredPayload = serialize(Target, payload);
      const res = await axiosInstance.post(ApiRoutes.TARGET, {
        sbti_target: desiredPayload,
      });
      Notification({
        message: 'Target created successfully',
        description: '',
        type: NotificationTypes.SUCCESS,
      });
      return res.data;
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchTargetCoverageMeta = async (isSme?: boolean) => {
    try {
      setMetaLoading(true);
      const params = {
        is_sme: isSme,
      };
      const response = await axiosInstance.get(ApiRoutes.TARGET_COVERAGE_META, {
        params,
      });

      const data = deserialize(TargetCoverageMeta, response.data.metadata);
      const desiredData = data as TargetCoverageMeta[];
      const mappedData = mapDesiredData(desiredData);
      setTargetCoverageMeta(mappedData as TargetCoverageMeta[]);
      return mappedData;
    } catch (error) {
    } finally {
      setMetaLoading(false);
    }
  };

  const resetTargetCoverageMeta = () => setTargetCoverageMeta([]);
  const resetTargetYearsMeta = () => setTargetYearsMeta([]);

  const fetchTargeYearsMeta = async (
    isSme?: boolean,
    targetType?: string,
    baselineYear?: number
  ) => {
    try {
      setMetaLoading(true);
      const params = {
        is_sme: isSme,
        target_type: targetType,
        baseline_year: baselineYear,
      };
      const response = await axiosInstance.get(ApiRoutes.TARGET_YEAR_META, {
        params,
      });
      const data = response.data.metadata;
      const yearsMeta = generateYearOptions(data);
      setTargetYearsMeta(yearsMeta as TargetYearsMeta[]);
    } catch (error) {
    } finally {
      setMetaLoading(false);
    }
  };

  const updateTarget = async (targetId: number, payload: Target) => {
    try {
      setLoading(true);
      const desiredPayload = serialize(Target, payload);
      const res = await axiosInstance.put(
        generatePath(ApiRoutes.UPDATE_TARGET, { targetId }),
        { sbti_target: desiredPayload }
      );
      Notification({
        message: 'Target updated successfully',
        description: '',
        type: NotificationTypes.SUCCESS,
      });
      return res.data;
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const deleteTarget = async (targetId: number) => {
    try {
      setLoading(true);
      await axiosInstance.delete(
        generatePath(ApiRoutes.UPDATE_TARGET, { targetId })
      );
      Notification({
        message: 'Target deleted successfully',
        description: '',
        type: NotificationTypes.SUCCESS,
      });
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const duplicateTarget = async (targetName?: string) => {
    try {
      const response = await axiosInstance.get(
        ApiRoutes.DUPLICATE_TARGET_NAME,
        {
          params: { target_name: targetName },
        }
      );
      return response?.data?.exists;
    } catch (error) {
    } finally {
    }
  };

  const getParentOrganization = async (type?: {
    isCustom?: boolean;
    isEmptyState?: boolean;
  }) => {
    try {
      type?.isCustom
        ? setIsCustomButtonLoading(true)
        : type?.isEmptyState
        ? setLoading(true)
        : setParentOrgLoading(true);
      const response = await axiosInstance.get(
        ApiRoutes.GET_PARENT_ORGANIZATION
      );
      const data = deserialize(ParentOrganization, response.data.organization);
      setOrganization(data);
      return data;
    } catch (error) {
    } finally {
      setParentOrgLoading(false);
      setIsCustomButtonLoading(false);
      setLoading(false);
    }
  };

  return {
    loading,
    metaLoading,
    createTarget,
    fetchTargetCoverageMeta,
    targetCoverageMeta,
    resetTargetCoverageMeta,
    targetYearsMeta,
    fetchTargeYearsMeta,
    updateTarget,
    deleteTarget,
    resetTargetYearsMeta,
    duplicateTarget,
    getParentOrganization,
    organization,
    parentOrgLoading,
    isCustomButtonLoading,
  };
};

export default TargetService;
